import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { Badge } from "../components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Users, Car, FileText, AlertCircle, DollarSign, Receipt } from "lucide-react";

const MONTHS_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

const formatCurrency = (v) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(v);

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/dashboard/stats`)
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Cargando...</div>;
  if (!stats) return <div className="text-gray-400">Error cargando datos</div>;

  const cards = [
    { icon: Users, label: "Inquilinos activos", value: stats.active_tenants, color: "text-[#002FA7]" },
    { icon: Car, label: "Plazas registradas", value: stats.total_spots, color: "text-[#002FA7]" },
    { icon: FileText, label: "Facturas este mes", value: stats.month_invoices, color: "text-emerald-600" },
    { icon: AlertCircle, label: "Facturas pendientes", value: stats.pending_invoices, color: "text-amber-600" },
    { icon: DollarSign, label: `Facturado ${stats.current_year}`, value: formatCurrency(stats.total_billed_year), color: "text-emerald-600" },
    { icon: Receipt, label: `IVA ${stats.current_year}`, value: formatCurrency(stats.total_vat_year), color: "text-[#002FA7]" },
  ];

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8">
        <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-gray-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Panel de control
        </h2>
        <p className="text-sm text-gray-500 mt-1">{MONTHS_ES[stats.current_month - 1]} {stats.current_year}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-6 mb-8" data-testid="stats-grid">
        {cards.map((c, i) => (
          <div key={i} className="stat-card" data-testid={`stat-card-${i}`}>
            <div className="flex items-center gap-3 mb-3">
              <c.icon className={`w-5 h-5 ${c.color}`} strokeWidth={1.5} />
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-gray-500">{c.label}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{c.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-sm" data-testid="recent-invoices-table">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-bold tracking-tight text-gray-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Ultimas facturas
          </h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">N.º</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Inquilino</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Periodo</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">Total</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Estado</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {stats.recent_invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-gray-400">No hay facturas todavia</TableCell>
              </TableRow>
            ) : (
              stats.recent_invoices.map((inv) => (
                <TableRow key={inv.id} className="hover:bg-gray-50 transition-colors">
                  <TableCell className="text-sm font-medium text-gray-900 py-4 px-4">{inv.invoice_number}</TableCell>
                  <TableCell className="text-sm text-gray-900 py-4 px-4">{inv.tenant_name}</TableCell>
                  <TableCell className="text-sm text-gray-600 py-4 px-4">{MONTHS_ES[inv.month - 1]} {inv.year}</TableCell>
                  <TableCell className="text-sm font-medium text-gray-900 py-4 px-4 text-right">{formatCurrency(inv.total_amount)}</TableCell>
                  <TableCell className="py-4 px-4">
                    <span className={`status-badge ${inv.status === "pagada" ? "status-pagada" : "status-pendiente"}`}>
                      {inv.status}
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
